import React from 'react';
import {
  CircleMarker,
  MapContainer,
  Polyline,
  TileLayer,
  Tooltip,
  useMapEvents,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  EARTH_DEFAULT_MAP_CENTER,
  EARTH_DEFAULT_MAP_ZOOM,
  EARTH_MAX_MAP_ZOOM,
  EARTH_MIN_MAP_ZOOM,
} from '../../../constants/games/zoomedInEarthGame';

const normalizeLongitude = (lng) => (((lng + 180) % 360 + 360) % 360) - 180;
const clampLatitude = (lat) => Math.max(-90, Math.min(90, lat));
const isFiniteCoordinate = (value) => Number.isFinite(Number(value));

const toLatLng = (lat, lng) => [clampLatitude(Number(lat)), normalizeLongitude(Number(lng))];

const GuessClickHandler = ({ disabled, onSelectGuess }) => {
  useMapEvents({
    click(event) {
      if (disabled) return;
      onSelectGuess({
        lat: clampLatitude(event.latlng.lat),
        lng: normalizeLongitude(event.latlng.lng),
      });
    },
  });
  return null;
};

const EarthGuessMap = ({
  guesses = [],
  selectedGuess,
  answer,
  onSelectGuess,
  disabled = false,
}) => {
  const submittedGuesses = Array.isArray(guesses)
    ? guesses.filter(
        (guess) =>
          isFiniteCoordinate(guess?.guessLat) && isFiniteCoordinate(guess?.guessLng),
      )
    : [];

  const selectedLatLng =
    isFiniteCoordinate(selectedGuess?.lat) && isFiniteCoordinate(selectedGuess?.lng)
      ? toLatLng(selectedGuess.lat, selectedGuess.lng)
      : null;

  const answerLatLng =
    isFiniteCoordinate(answer?.lat) && isFiniteCoordinate(answer?.lng)
      ? toLatLng(answer.lat, answer.lng)
      : null;

  const latestSubmittedGuess = submittedGuesses.length > 0
    ? submittedGuesses[submittedGuesses.length - 1]
    : null;
  const latestSubmittedLatLng = latestSubmittedGuess
    ? toLatLng(latestSubmittedGuess.guessLat, latestSubmittedGuess.guessLng)
    : null;
  const lineStartLatLng = latestSubmittedLatLng || selectedLatLng;

  return (
    <div className="earth-map-wrap">
      <MapContainer
        className="earth-map"
        center={[EARTH_DEFAULT_MAP_CENTER.lat, EARTH_DEFAULT_MAP_CENTER.lng]}
        zoom={EARTH_DEFAULT_MAP_ZOOM}
        minZoom={EARTH_MIN_MAP_ZOOM}
        maxZoom={EARTH_MAX_MAP_ZOOM}
        scrollWheelZoom
        worldCopyJump
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <GuessClickHandler disabled={disabled} onSelectGuess={onSelectGuess} />

        {submittedGuesses.map((guess, index) => {
          const markerLatLng = toLatLng(guess.guessLat, guess.guessLng);
          const parsedAttempt = Number(guess.attemptNumber);
          const label = Number.isFinite(parsedAttempt) && parsedAttempt > 0
            ? Math.trunc(parsedAttempt)
            : index + 1;

          return (
            <CircleMarker
              key={`earth-submitted-guess-${guess.attemptNumber}-${guess.guessLat}-${guess.guessLng}`}
              center={markerLatLng}
              radius={8}
              pathOptions={{ color: '#4da3ff', fillColor: '#4da3ff', fillOpacity: 0.7 }}
            >
              <Tooltip direction="top" offset={[0, -8]} permanent>
                {label}
              </Tooltip>
            </CircleMarker>
          );
        })}

        {selectedLatLng && (
          <CircleMarker
            center={selectedLatLng}
            radius={7}
            pathOptions={{ color: '#9fd277', fillColor: '#9fd277', fillOpacity: 0.85 }}
          >
            <Tooltip direction="top" offset={[0, -8]} permanent={false}>
              Pending guess
            </Tooltip>
          </CircleMarker>
        )}

        {answerLatLng && (
          <CircleMarker
            center={answerLatLng}
            radius={8}
            pathOptions={{ color: '#ff5b5b', fillColor: '#ff5b5b', fillOpacity: 0.75 }}
          >
            <Tooltip direction="top" offset={[0, -8]} permanent={false}>
              Correct location
            </Tooltip>
          </CircleMarker>
        )}

        {lineStartLatLng && answerLatLng && (
          <Polyline positions={[lineStartLatLng, answerLatLng]} pathOptions={{ color: '#ffd166', weight: 2 }} />
        )}
      </MapContainer>
    </div>
  );
};

export default React.memo(EarthGuessMap);
